<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAuth, useUser } from '@clerk/vue';
import Layout from '../components/layout/Layout.vue';
import CyberLoading from '../components/ui/CyberLoading.vue';
import StellarBackground from '../components/animations/StellarBackground.vue';
import { useToNumber } from '@vueuse/core';
import { useCateringStore } from '@/stores/cateringStore';
import { useCateringOpenOrdersStore } from '@/stores/cateringOpenOrdersStore';

const router = useRouter();
const { isSignedIn, isLoaded, getToken } = useAuth();

// Redirect to sign-in if not authenticated
const checkAuth = computed( () => {
  if (isLoaded.value && !isSignedIn.value) {
    setTimeout(() => {
      router.push({
      path: '/sign-in',
      query: { redirect: '/alterbyte' }
    });
    }, 1200);
    return false;
  }
  if (categories.value.length > 0 && products.value.length > 0 && user.value) {
    isLoading.value = false;
  }
  return true;
});

const { t } = useI18n()

const categories = ref([]);
const products = ref([]);
const user = ref({});

const isCrew = ref(false);

const cateringStore = useCateringStore();
const cateringOpenOrdersStore = useCateringOpenOrdersStore();
const cart = computed(() => cateringStore.cart);
const deliveryFee = cateringStore.deliveryFee;
const isDelivery = computed({
  get: () => cateringStore.isDelivery,
  set: (value) => cateringStore.isDelivery = value
});
const isCartOpen = computed({
  get: () => cateringStore.isCartOpen,
  set: (value) => cateringStore.isCartOpen = value
});
const showAddCoinsDialog = computed({
  get: () => cateringStore.showAddCoinsDialog,
  set: (value) => cateringStore.showAddCoinsDialog = value
});
const productQuantities = computed(() => cateringStore.productQuantities);

// Update getData function to handle new API response format
async function getData() {
    const categoriesResponse = await fetch(`https://api.alterlan.be/v1/product-categories`);
    const productsResponse = await fetch(`https://api.alterlan.be/v1/products`);
    await getUser();

    const categoriesData = await categoriesResponse.json();
    const productsData = await productsResponse.json();
    
    categories.value = categoriesData;
    
    // Log the raw products data to check supplements
    // console.log('Raw products data:', productsData);
    
    // Ensure supplements are properly initialized even if they're empty
    products.value = productsData.map(product => ({
      ...product,
      supplements: product.supplements || []
    }));
}

async function getUser() {
  const userResponse = await fetch('https://api.alterlan.be/v1/users/me', {headers: {Authorization: `Bearer ${await getToken.value()}`}});
  user.value = await userResponse.json();
  isCrew.value = user.value.roles.includes("CREW");
  return user.value;
}

getData().then(() => {
  initializeQuantities();
});

// Initialize quantities for available products
const initializeQuantities = () => {
  cateringStore.initializeQuantities(products.value);
};

// Watch for products changes to initialize quantities
watch(products, initializeQuantities, { immediate: true });

const addToCart = (product) => {
  // console.log('Adding product:', product);
  // console.log('Has supplements?', product.supplements);
  // console.log('Supplements length:', product.supplements?.length);
  if (product.availability !== "AVAILABLE") {
    return;
  }

  if (product.supplements && product.supplements.length > 0) {
    // console.log('Opening supplements modal');
    selectedProduct.value = product;
    selectedSupplements.value = [];
    showSupplementsDialog.value = true;
  } else {
    // console.log('Adding directly to cart');
    cateringStore.addToCart(product, 1);
  }
};

const removeFromCart = (product) => {
  const index = cart.value.findIndex(item => 
    item.id === product.id && 
    JSON.stringify(item.supplements) === JSON.stringify(product.supplements)
  );
  if (index !== -1) {
    if (cart.value[index].quantity > 1) {
      cart.value[index].quantity -= 1;
    } else {
      cart.value.splice(index, 1);
    }
  }
};

const cartTotal = computed(() => {
  const itemsTotal = cart.value.reduce((total, item) => {
    const itemPrice = item.price + item.supplements.reduce((suppTotal, supp) => suppTotal + supp.price, 0);
    return total + (itemPrice * item.quantity);
  }, 0);
  return itemsTotal + (cart.value.length > 0 && isDelivery.value ? deliveryFee : 0);
});


const sortedCartItems = computed(() => {
  const beverages = cart.value.filter(item => {
    const product = products.value.find(p => p.id === item.id);
    return product && product.category.name.en === "Beverages";
  });
  
  const food = cart.value.filter(item => {
    const product = products.value.find(p => p.id === item.id);
    return product && product.category.name.en !== "Beverages";
  });
  
  return { beverages, food };
});

const eventID = "26cd0154-25f7-48be-a44c-a87e6b585b9c";
let orderUpdateInterval: number | null = null;

// Request notification permissions
const requestNotificationPermission = async () => {
  if (!("Notification" in window)) {
    console.log("This browser does not support notifications");
    return;
  }

  if (Notification.permission === "default") {
    try {
      const permission = await Notification.requestPermission();
      console.log("Notification permission:", permission);
    } catch (error) {
      console.error("Error requesting notification permission:", error);
    }
  }
};

// Fetch open orders on component mount
const fetchOpenOrders = async () => {
  if (!isSignedIn.value) return;
  
  try {
    const response = await fetch('https://api.alterlan.be/v1/orders/me', {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${await getToken.value()}`
      }
    });
    if (!response.ok) {
      throw new Error('Failed to fetch open orders');
    }
    const data = await response.json();
    cateringOpenOrdersStore.setOrders(data);
  } catch (error) {
    console.error('Error fetching open orders:', error);
  }
};

const startOrderUpdates = () => {
  // Clear any existing interval
  if (orderUpdateInterval) {
    clearInterval(orderUpdateInterval);
  }
  
  // Initial fetch
  fetchOpenOrders();
  
  // Set up interval for updates
  orderUpdateInterval = setInterval(fetchOpenOrders, 5000);
};

onMounted(() => {
  requestNotificationPermission();
  if (isSignedIn.value) {
    startOrderUpdates();
  }
});

// Clean up interval when component is unmounted
onUnmounted(() => {
  if (orderUpdateInterval) {
    clearInterval(orderUpdateInterval);
    orderUpdateInterval = null;
  }
});

watch(isSignedIn, (newValue) => {
  if (newValue) {
    startOrderUpdates();
  } else if (orderUpdateInterval) {
    clearInterval(orderUpdateInterval);
    orderUpdateInterval = null;
  }
});


async function getInputUserId(input: string) {
  let userId = "";
  try {
    const response = await fetch(`https://api.alterlan.be/v1/users/from-ticket/${input}`, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${await getToken.value()}`
      }
    });
    if (!response.ok) {
      throw new Error('Failed to fetch users');
    }
    const data = await response.json();
    userId = data.id;
  } catch (error) {
    console.error('Error fetching users:', error);
  }
  return userId;
}

const getOrderStatus = async () => {
  if (!isSignedIn.value) return;
  
  try {
    const response = await fetch('https://api.alterlan.be/v1/orders/me', {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${await getToken.value()}`
      }
    });
    if (!response.ok) {
      throw new Error('Failed to fetch orders');
    }
    const data = await response.json();
    cateringOpenOrdersStore.setOrders(data);
  } catch (error) {
    console.error('Error fetching orders:', error);
  }
};

async function placeOrder() {
  // Expand cart items with quantities into individual items with supplements
  const expandedCart = cart.value.flatMap(item => {
    if (item.supplements.length > 0) {
      return Array(item.quantity).fill(null).map(() => ({
        productId: item.id,
        supplements: item.supplements.map(s => s.id)
      }));
    } else {
      return Array(item.quantity).fill(null).map(() => ({
        productId: item.id,
      }));
    }
  });

  const user = await getUser();
  console.log("user Wallet:", user.walletBalance, cartTotal.value);

  if (user.walletBalance >= cartTotal.value) {
    try {
      let userId = null;
      if (isCrew.value && inputUserCode.value) {
        userId = await getInputUserId(inputUserCode.value); // Await the API call
      }

      const response = await fetch('https://api.alterlan.be/v1/orders', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await getToken.value()}`
        },
        body: JSON.stringify({
          eventId: eventID,
          userId: userId,
          customerNote: orderComment.value,
          delivery: isDelivery.value,
          deliveryLocation: isDelivery.value ? deliveryLocation.value : null,
          orderLines: expandedCart
        })
      });

      if (!response.ok) {
        throw new Error('Failed to place order');
      }

      const data = await response.json();
      console.log('Order placed successfully:', data);

      // Add the new order to the open orders store
      cateringOpenOrdersStore.addOrder(data);
      console.log('Open orders:', cateringOpenOrdersStore.openOrders);

      await getUser();

      // Reset all cart-related state
      cateringStore.$reset();
      cateringStore.initializeQuantities(products.value);
      inputUserCode.value = '';
      orderComment.value = '';
      deliveryLocation.value = '';
      showSuccesMessage();

      setTimeout(() => {
        getOrderStatus();
      }, 1000);
    } catch (error) {
      console.error('Error placing order:', error);
      showFailedMessage('Failed to place order');
    }
  } else {
    showFailedMessage('Not enough ALTERCOINS');
  }
}

const confirmDialogMessage = ref('');
const confirmDialogConfirmText = ref('');
const confirmDialogCancelText = ref('');

const showSuccesMessage = () => {
  //Modal message displaying success
  showConfirmDialog.value = true;
  confirmDialogMessage.value = 'Order placed successfully';
  confirmDialogCancelText.value = 'OK';
}
// Add succes message for coins
const showCoinSuccesMessage = () => {
  showConfirmDialog.value = true;
  confirmDialogMessage.value = 'Coins have been added to the users wallet';
  confirmDialogCancelText.value = 'OK';
}

const showFailedMessage = (message: string) => {
  //Modal message displaying failed
  showConfirmDialog.value = true;
  confirmDialogMessage.value = message || 'Failed to place order';
  confirmDialogCancelText.value = 'Cancel';
}

const checkAge = (restriction: 'NONE' | 'AGE_16' | 'AGE_18'): boolean => {
  if (restriction === 'NONE') return true;
  if (!user.value?.dateOfBirth) return false;

  // Parse yyyy-mm-dd format
  const [year, month, day] = user.value.dateOfBirth.split('-').map(Number);
  const birthDate = new Date(year, month - 1, day); // month is 0-based in Date constructor
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  
  // Adjust age if birthday hasn't occurred this year
  const hasHadBirthday = today.getMonth() > birthDate.getMonth() || 
    (today.getMonth() === birthDate.getMonth() && today.getDate() >= birthDate.getDate());
  
  if (!hasHadBirthday) {
    age--;
  }
  
  return restriction === 'AGE_16' ? age >= 16 : age >= 18;
};

const getAvailableStock = (product) => {
  // First check age restriction
  if (!checkAge(product.ageRestriction)) {
    return 0;
  }

  if (product.stockQuantity === -1 || product.stockQuantity > 10) return null;
  if (product.stockQuantity === null) return null;
  const inCart = cart.value.find(item => item.id === product.id)?.quantity || 0;
  return product.stockQuantity - inCart;
};

const toggleCart = () => {
  isCartOpen.value = !isCartOpen.value;
};

const closeCartOnOutsideClick = (event) => {
  // Only close if clicking outside the cart container
  if (event.target.closest('.cart-container')) {
    return;
  }
  isCartOpen.value = false;
};

const isLoading = ref(true);
const inputUserCode = ref('');
const showConfirmDialog = ref(false);
const showSupplementsDialog = ref(false);
const selectedProduct = ref(null);
const selectedSupplements = ref([]);
const addCoinsUserIput = ref('');
const selectedAmount = ref(null);
const selectedPaymentMethod = ref(null);
const orderComment = ref('');
const deliveryLocation = ref('');
const showOpenOrdersModal = ref(false);

const coinPackages = [
  { coins: 5, price: 8.75, variationId: "596301" },
  { coins: 10, price: 17.50, variationId: "589261" },
  { coins: 25, price: 42.00, variationId: "589290" },
  { coins: 50, price: 77.00, variationId: "589291" }
];

const paymentMethods = ['CASH', 'CARD'];

const openAddCoinsDialog = () => {
  showAddCoinsDialog.value = true;
  addCoinsUserIput.value = '';
  selectedAmount.value = null;
  selectedPaymentMethod.value = null;
};

const closeAddCoinsDialog = () => {
  showAddCoinsDialog.value = false;
};

const handleAddCoins = async () => {
  if (!addCoinsUserIput.value || !selectedAmount.value || !selectedPaymentMethod.value) {
    return;
  }
  
  // TODO: Implement the actual coins addition logic
  // console.log('Adding coins:', {
  //   userId: getInputUserId(addCoinsUserIput.value),
  //   eventId: eventID,
  //   paymentMethod: selectedPaymentMethod.value,
  //   variationId: coinPackages.find(pkg => pkg.coins === selectedAmount.value)?.variationId
  // });

  // add api /v1/pretix/top-up-order?test=true
  fetch('https://api.alterlan.be/v1/pretix/top-up-order', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${await getToken.value()}`
    },
    body: JSON.stringify({
      userId: getInputUserId(addCoinsUserIput.value),
      // userId: addCoinsUserIput.value,
      eventId: eventID,
      variationId: coinPackages.find(pkg => pkg.coins === selectedAmount.value)?.variationId,
      paymentMethod: selectedPaymentMethod.value     
    })
  }).then(response => {
    if (!response.ok) {
      throw new Error('Failed to add coins');
    }
    return response.json();
  }).then(data => {
    console.log('Coins added successfully:', data);
    showCoinSuccesMessage();
  }).catch(error => {
    console.error('Error adding coins:', error);
    showFailedMessage('Failed to add coins, please try again. If this error persists, please contact Development.');
  });

  closeAddCoinsDialog();
};

const handleOrder = () => {
  // Check if delivery is selected but seat number is empty
  if (isDelivery.value && !deliveryLocation.value.trim()) {
    showFailedMessage('Please enter your seat number for delivery');
    return;
  }

  if (isCrew.value && !inputUserCode.value) {
    showConfirmDialog.value = true;
    confirmDialogMessage.value = 'Are you placing this order for yourself?';
    confirmDialogConfirmText.value = 'Yes, for me';
    confirmDialogCancelText.value = 'No';
  } else {
    placeOrder();
  }
};

const confirmSelfOrder = () => {
  showConfirmDialog.value = false;
  confirmDialogMessage.value = '';
  confirmDialogConfirmText.value = '';
  confirmDialogCancelText.value = '';
  placeOrder();
};

const cancelOrder = () => {
  showConfirmDialog.value = false;
  confirmDialogMessage.value = '';
  confirmDialogConfirmText.value = '';
  confirmDialogCancelText.value = '';
};

const confirmSupplements = () => {
  if (selectedProduct.value) {
    // Add the main product with selected supplements
    cateringStore.addToCart(selectedProduct.value, 1, selectedSupplements.value);
    // Reset and close modal
    selectedProduct.value = null;
    selectedSupplements.value = [];
    showSupplementsDialog.value = false;
  }
};

const toggleSupplement = (supplement) => {
  const index = selectedSupplements.value.findIndex(s => s.id === supplement.id);
  if (index === -1) {
    selectedSupplements.value.push(supplement);
  } else {
    selectedSupplements.value.splice(index, 1);
  }
};

const cancelSupplements = () => {
  selectedProduct.value = null;
  selectedSupplements.value = [];
  showSupplementsDialog.value = false;
};

const scrollToCategory = (categoryId: string) => {
  const element = document.getElementById('category-' + categoryId);
  const menuSection = document.querySelector('.menu-section');
  
  if (element && menuSection) {
    // Get the height of the category navigation
    const categoryNav = document.querySelector('.category-nav');
    const navHeight = categoryNav ? categoryNav.getBoundingClientRect().height : 0;
    
    // Get the element's position relative to the menu section
    const elementTop = element.offsetTop;
    
    // Scroll the menu section with smooth behavior
    menuSection.scrollTo({
      top: elementTop - navHeight - 7,
      behavior: 'smooth'
    });
  }
};

const formatTime = (timeString) => {
  if (!timeString) return '';
  return timeString.slice(0, 5); // Convert "HH:MM:SS" to "HH:MM"
};

const formatTimeDiff = (createdAt: string) => {
  const created = new Date(createdAt);
  const now = new Date();
  const diffMs = now.getTime() - created.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const hours = Math.floor(diffMins / 60);
  const mins = diffMins % 60;
  
  const createdTime = created.toLocaleTimeString('en-US', { 
    hour: '2-digit', 
    minute: '2-digit',
    hour12: false 
  });
  
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')} (${createdTime})`;
};

const isWholeDay = (category) => {
  // Check if the category spans a full day or close to it
  // This assumes that a category spanning 21 hours or more is considered "whole day"
  if (!category.startTime || !category.endTime) return true;
  
  const start = new Date(`2000-01-01T${category.startTime}`);
  const end = new Date(`2000-01-01T${category.endTime}`);
  
  // If end time is before start time, add 24 hours to end time
  if (end < start) {
    end.setHours(end.getHours() + 24);
  }
  
  const diffHours = (end - start) / (1000 * 60 * 60);
  return diffHours >= 21;
};

const shouldShowTime = (category) => {
  // Only show time for General category, hide for Beverages
  if (category.name.en === "Beverages") return false;
  if (category.name.en === "General") return true;
  return !isWholeDay(category);
};

const sortedCategories = computed(() => {
  return [...categories.value].sort((a, b) => {
    return (a.position || 999) - (b.position || 999);
  });
});

watch(cart, (newCart) => {
  // Only initialize quantities, don't close cart
  if (newCart.length === 0) {
    cateringStore.initializeQuantities(products.value);
  }
}, { deep: true });

const showNotificationPrompt = ref(true);
const notificationPermission = ref(Notification.permission);

onMounted(() => {
  // Check if we've already shown the prompt
  const hasShownPrompt = localStorage.getItem('hasShownNotificationPrompt');
  if (hasShownPrompt) {
    showNotificationPrompt.value = false;
  }
});

const dismissNotificationPrompt = () => {
  showNotificationPrompt.value = false;
  localStorage.setItem('hasShownNotificationPrompt', 'true');
};
</script>

<template>
  <Layout>
    <!-- Notification Prompt Overlay -->
    <div v-if="showNotificationPrompt && notificationPermission === 'default' && checkAuth" class="notification-overlay">
      <div class="notification-prompt">
        <h3>Enable Notifications</h3>
        <p>Please allow notifications to get updates when your order is ready!</p>
        <button @click="dismissNotificationPrompt" class="dismiss-button">Got it!</button>
      </div>
    </div>

    <div class="catering-wrapper">
      <!-- Add Coins Button -->
      <a 
        v-if="!isCrew && checkAuth" 
        href="https://shop.alterlan.be/ALTERLAN0.2/#category-154978" 
        target="_blank" 
        class="add-coins-button"
      >
        ADD ALTERCOINS
      </a>
      <button 
        v-else-if="checkAuth" 
        @click="openAddCoinsDialog" 
        class="add-coins-button"
      >
        ADD ALTERCOINS
      </button>
      <button v-if="checkAuth && cateringOpenOrdersStore.sortedOpenOrders.length > 0" 
              @click="showOpenOrdersModal = true" 
              class="view-orders-button">
        View Orders ({{ cateringOpenOrdersStore.sortedOpenOrders.length }})
      </button>
      <div class="catering-header" v-if="checkAuth">
        <h1 class="catering-title">ALTERBYTE</h1>
        <p>What are you craving now, <span style="color: #df824d">{{user.firstName}}</span>?</p>
        <div class="mobile-buttons">
          <a 
            v-if="!isCrew" 
            href="https://shop.alterlan.be/ALTERLAN0.2/#category-154978" 
            target="_blank" 
            class="add-coins-button"
          >
            ADD ALTERCOINS
          </a>
          <button 
            v-else
            @click="openAddCoinsDialog" 
            class="add-coins-button"
          >
            ADD ALTERCOINS
          </button>
          <button v-if="cateringOpenOrdersStore.sortedOpenOrders.length > 0" 
                  @click="showOpenOrdersModal = true" 
                  class="view-orders-button">
            View Orders ({{ cateringOpenOrdersStore.sortedOpenOrders.length }})
          </button>
        </div>
      </div>
    
      <div class="catering-container">
      <StellarBackground backColor="#2d3434" :circle-offset="{ x: 0, y: 50 }" class="stellar-bg" />
      <CyberLoading :is-loading="isLoading" />

      
      <!-- Cart Toggle Button (Mobile) -->
      <button class="cart-toggle" @click="toggleCart" v-if="checkAuth">
        <v-icon color="white" size="24">mdi-cart-outline</v-icon>
        <span class="cart-count" v-if="cart.length">{{ cart.length }}</span>
      </button>

      <div class="menu-wrapper" v-if="checkAuth">
        <!-- Category Navigation -->
        <div class="category-nav">
          <div class="category-buttons-container">
            <button 
              v-for="category in sortedCategories" 
              :key="category.id"
              @click="scrollToCategory(category.id)"
              class="category-nav-button"
            >
              {{ category.name.en }}
            </button>
          </div>
          <div class="altercoin-conversion">
            <v-icon>mdi-compare-horizontal</v-icon>
            <p>1 ALTERCOIN = € 1,75</p>
          </div>
        </div>

        <div class="menu-section">
          <div v-for="category in sortedCategories" :key="category.id" class="category-section" :id="'category-' + category.id">
            <h2>
              {{ category.name.en }}
              <span v-if="shouldShowTime(category)" class="category-time">
                {{ formatTime(category.startTime) }} - {{ formatTime(category.endTime) }}
              </span>
            </h2>
            <div class="products-grid">
              <div v-for="product in products.filter(p => p.category.id === category.id)" 
                   :key="product.id" 
                   class="product-card">
                <div class="product-info">
                  <div class="product-header">
                    <span class="product-name">{{ product.name.en }}</span>
                    <span class="product-price">{{ product.price }} ALTERCOINS</span>
                  </div>
                  <div class="product-status">
                    <span v-if="product.ageRestriction !== 'NONE'" class="age-restriction">
                      {{ product.ageRestriction === 'AGE_16' ? '16+' : '18+' }}
                    </span>
                    <span v-if="!checkAge(product.ageRestriction)" class="age-restriction-string">
                      Age restricted
                    </span>
                    <span v-if="product.stockQuantity !== null && product.stockQuantity !== -1 && product.stockQuantity <= 10 && product.availability === 'AVAILABLE'" 
                          :class="['stock-info', { 'out-of-stock': getAvailableStock(product) === 0 }]">
                      {{ getAvailableStock(product) > 0 
                         ? `${getAvailableStock(product)} left in stock`
                         : 'Out of stock' }}
                    </span>
                    <span v-else-if="product.availability === 'TEMPORARILY_UNAVAILABLE' || product.availability === 'UNAVAILABLE'" 
                          :class="product.availability === 'UNAVAILABLE' ? 'out-of-stock' : 'temporarily-unavailable'">
                      {{ product.availability === 'TEMPORARILY_UNAVAILABLE' ? 'Temporarily unavailable' : 'Out of stock' }}
                    </span>
                  </div>
                </div>
                <div class="product-actions">
                  <button 
                    @click="addToCart(product)" 
                    class="add-button"
                    :disabled="
                      !checkAge(product.ageRestriction) || product.availability !== 'AVAILABLE' ||
                      (product.stockQuantity !== -1 && product.stockQuantity !== null && (
                        getAvailableStock(product) === 0
                      ))
                    "
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="checkAuth" :class="['cart-section', { 'mobile-cart': true, 'is-open': isCartOpen }]"
           @click="closeCartOnOutsideClick">
        <div class="cart-container">
            <div class="cart-header">
          <h2>Cart</h2>
          <div class="wallet-balance" title="Available balance">
            <span><v-icon size="18" color="#76c4ae">mdi-wallet</v-icon> {{user.walletBalance}} ALTERCOINS</span>
          </div>
          <!-- add small close button -->
          <button @click="toggleCart" class="close-button">
            <v-icon>mdi-close</v-icon>
          </button>
        </div>
          <div class="cart-items">
            <template v-if="sortedCartItems.beverages.length > 0">
              <div class="cart-category">
                <h3>Beverages</h3>
                <div v-for="item in sortedCartItems.beverages" :key="item.id" class="cart-item">
                  <div class="item-info">
                    <span>{{ item.name }}</span>
                    <div v-if="item.supplements.length" class="supplements-list">
                      <span v-for="supplement in item.supplements" :key="supplement.id" class="supplement-item">
                        + {{ supplement.name }} ({{ supplement.price }} ALTERCOINS)
                      </span>
                    </div>
                    <span>{{ item.price + item.supplements.reduce((total, s) => total + s.price, 0) }} ALTERCOINS × {{ item.quantity }}</span>
                  </div>
                  <button @click.stop="removeFromCart(item)" class="remove-button">Remove</button>
                </div>
              </div>
            </template>
            
            <template v-if="sortedCartItems.food.length > 0">
              <div class="cart-category">
                <h3>Food</h3>
                <div v-for="item in sortedCartItems.food" :key="item.id" class="cart-item">
                  <div class="item-info">
                    <span>{{ item.name }}</span>
                    <div v-if="item.supplements.length" class="supplements-list">
                      <span v-for="supplement in item.supplements" :key="supplement.id" class="supplement-item">
                        + {{ supplement.name }} ({{ supplement.price }} ALTERCOINS)
                      </span>
                    </div>
                    <span>{{ item.price + item.supplements.reduce((total, s) => total + s.price, 0) }} ALTERCOINS × {{ item.quantity }}</span>
                  </div>
                  <button @click.stop="removeFromCart(item)" class="remove-button">Remove</button>
                </div>
              </div>
            </template>
          </div>
          <div class="cart-summary">
            <div class="delivery-section">
              <div class="delivery-fee">
                <label for="delivery-toggle">
                  <input type="checkbox" v-model="isDelivery" id="delivery-toggle">
                  Delivery
                  <span>+ {{ cart.length > 0 && isDelivery ? deliveryFee : 1 }} ALTERCOINS</span>
                </label>
              </div>
              <div class="seat-input">
                <input 
                  type="text" 
                  id="delivery-location" 
                  v-model="deliveryLocation" 
                  placeholder="Seat"
                  class="user-id-field"
                  :disabled="!isDelivery"
                  :class="{'disabled-field': !isDelivery}"
                >
              </div>
            </div>
            <!-- <div v-if="!isDelivery" class="disabled-field-indicator">
              <v-icon small>mdi-lock</v-icon>
              <span>Check delivery to fill in your seat</span>
            </div> -->
            <div class="user-id-input">
              <label for="target-user">User ID:</label>
              <input 
                type="text" 
                id="target-user" 
                v-model="inputUserCode" 
                placeholder="Enter user ID"
                class="user-id-field"
              >
            </div>
            <div class="comment-input">
              <label for="comment">Comment:</label>
              <input 
                type="text" 
                id="comment" 
                v-model="orderComment" 
                placeholder="Enter comment"
                class="user-id-field"
              >
            </div>
            <div class="total">
              <span>Total:</span>
              <span>{{ cartTotal }} ALTERCOINS</span>
            </div>
            <button @click="handleOrder" class="order-button" :disabled="cart.length === 0">
              Order
            </button>
          </div>
        </div>
      </div>
    </div>
    </div>

    <!-- Confirmation Dialog -->
    <div v-if="showConfirmDialog" class="confirmation-dialog">
      <div class="dialog-content">
        <p>{{ confirmDialogMessage }}</p>
        <div class="dialog-buttons">
          <button @click="confirmSelfOrder" v-if="confirmDialogConfirmText" class="confirm-button">{{ confirmDialogConfirmText }}</button>
          <button @click="cancelOrder" v-if="confirmDialogCancelText" class="cancel-button">{{ confirmDialogCancelText }}</button>
        </div>
      </div>
    </div>

    <!-- Supplements Dialog -->
    <div v-if="showSupplementsDialog" class="confirmation-dialog">
      <div class="dialog-content supplements-dialog">
        <h2>SELECT SUPPLEMENTS</h2>
        <div class="supplements-content">
          <div v-if="selectedProduct" class="product-info">
            <span class="product-name">{{ selectedProduct.name.en }}</span>
            <span class="product-price">{{ selectedProduct.price }} ALTERCOINS</span>
          </div>
          
          <div class="supplements-list">
            <div v-for="supplement in selectedProduct?.supplements" 
                :key="supplement.id"
                :class="['supplement-option', { active: selectedSupplements.findIndex(s => s.id === supplement.id) !== -1 }]"
                @click="toggleSupplement(supplement)"
            >
              <span class="supplement-name">{{ supplement.name.en }}</span>
              <span class="supplement-price">{{ supplement.price }} ALTERCOINS</span>
            </div>
          </div>

          <div class="dialog-buttons">
            <button 
              @click="confirmSupplements" 
              class="confirm-button"
            >
              Add to Cart
            </button>
            <button @click="cancelSupplements" class="cancel-button">Cancel</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Add Coins Dialog -->
    <div v-if="showAddCoinsDialog" class="confirmation-dialog">
      <div class="dialog-content add-coins-dialog">
        <!-- close button -->
        <button @click="closeAddCoinsDialog" class="close-button">
          <v-icon>mdi-close</v-icon>
        </button>
        <h2>ADD ALTERCOINS</h2>
        
        <div class="input-group">
          <label for="user-id">Scan User ID:</label>
          <input 
            type="text" 
            id="user-id" 
            v-model="addCoinsUserIput"
            placeholder="Scan or enter user ID"
            class="user-id-field"
          >
        </div>

        <div class="coin-packages">
          <h3>Select Amount:</h3>
          <div class="package-buttons">
            <button 
              v-for="pkg in coinPackages" 
              :key="pkg.coins"
              :class="['package-button', { active: selectedAmount === pkg.coins }]"
              @click="selectedAmount = pkg.coins"
            >
              {{ pkg.coins }} ALTERCOINS
              <span class="price">€{{ pkg.price.toFixed(2) }}</span>
            </button>
          </div>
        </div>

        <div class="payment-methods">
          <h3>Payment Method:</h3>
          <div class="method-buttons">
            <button 
              v-for="method in paymentMethods" 
              :key="method"
              :class="['method-button', { active: selectedPaymentMethod === method }]"
              @click="selectedPaymentMethod = method"
            >
              {{ method }}
            </button>
          </div>
        </div>

        <div class="dialog-buttons">
          <button 
            @click="handleAddCoins" 
            class="confirm-button"
            :disabled="!addCoinsUserIput || !selectedAmount || !selectedPaymentMethod"
          >
            Add Coins
          </button>
          <button @click="closeAddCoinsDialog" class="cancel-button">Cancel</button>
        </div>
      </div>
    </div>

    <!-- Open Orders Modal -->
    <div v-if="showOpenOrdersModal" class="modal-overlay" @click="showOpenOrdersModal = false">
      <div class="modal-content orders-modal" @click.stop>
        <div class="modal-header">
          <h2>Your orders</h2>
          <button class="close-button" @click="showOpenOrdersModal = false">&times;</button>
        </div>
        <div class="orders-list">
          <div v-if="cateringOpenOrdersStore.sortedOpenOrders.length === 0" class="no-orders">
            No open orders
          </div>
          <div v-else>
            <div v-for="order in cateringOpenOrdersStore.sortedOpenOrders" :key="order.id" class="order-card">
              <div class="order-header">
                <span class="order-number">#{{ order.orderNumber }}</span>
                <span class="order-time">{{ formatTimeDiff(order.createdAt) }}</span>
              </div>
              
              <!-- Group by category -->
              <template v-if="order.orderLines.some(line => line.product.category.name.en === 'Beverages')">
                <div class="category-section">
                  <h3>Beverages</h3>
                  <div v-for="item in order.orderLines.filter(line => line.product.category.name.en === 'Beverages')" 
                       :key="item.product.id" 
                       class="order-item">
                    <span class="item-quantity">{{ item.quantity }}x</span>
                    <div class="item-details">
                      <span class="item-name">{{ item.product.name.en }}</span>
                      <div v-if="item.supplements?.length" class="supplements-list">
                        <span v-for="supplement in item.supplements" :key="supplement.id" class="supplement-item">
                          + {{ supplement.name.en }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </template>

              <template v-if="order.orderLines.some(line => line.product.category.name.en !== 'Beverages')">
                <div class="category-section">
                  <h3>Food</h3>
                  <div v-for="item in order.orderLines.filter(line => line.product.category.name.en !== 'Beverages')" 
                       :key="item.product.id" 
                       class="order-item">
                    <span class="item-quantity">{{ item.quantity }}x</span>
                    <div class="item-details">
                      <span class="item-name">{{ item.product.name.en }}</span>
                      <div v-if="item.supplements?.length" class="supplements-list">
                        <span v-for="supplement in item.supplements" :key="supplement.id" class="supplement-item">
                          + {{ supplement.name.en }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </template>

              <div class="order-footer">
                <span class="order-total">Total: {{ order.totalPrice.toFixed(2) }} ALTERCOINS</span>
                <span :class="['order-status', order.status.toLowerCase()]">{{ order.status }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style scoped>
.catering-wrapper {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 80px);
  width: 100vw;
  overflow: hidden;
}

.catering-header {
  text-align: center;
  position: relative;
  margin-bottom: 0.5rem;
  margin-top: 2rem;
  z-index: 5;
}

.catering-header h1 {
  font-family: 'Airborne GP', sans-serif;
  font-size: 3.5rem;
  font-weight: bold;
  text-transform: uppercase;
  color: #76c4ae;
  letter-spacing: 4px;
  position: relative;
  display: inline-block;
}

.catering-header p {
  font-size: 1.2rem;
  width: 90%;
  color: #ccc;
  margin: 0 auto;
  line-height: 1.6;
  position: relative;
  text-shadow: 0 0 10px rgba(255, 255, 255, 0.1);
}

.catering-container {
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 2rem;
  height: 100%;
  padding: 1rem;
  max-width: 1400px;
  width: 100%;
  margin: 0 auto;
  overflow-y: hidden;
  position: relative;
}

.cart-toggle {
  display: none;
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: #df824d;
  color: white;
  cursor: pointer;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  z-index: 1000;
}

.cart-icon {
  font-size: 24px;
}

.cart-count {
  position: absolute;
  top: -5px;
  right: -5px;
  background: #76c4ae;
  color: white;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
}

.cart-header {
  display: flex;
  padding-bottom: 1rem;
  border-bottom: 1px solid #df824d;
  color: #df824d !important;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid rgba(223, 130, 77, 0.5);
}

.cart-section .cart-header span {
    font-size: 0.9rem;
    background: rgba(118, 196, 174, 0.15);
    padding: 0.3rem 0.6rem;
    border-radius: 4px;
    border: 1px solid rgba(118, 196, 174, 0.3);
}

.close-cart {
  display: none;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
}

.sign-out-button {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  width: 100px;
  height: 24px;
  background: #76c4ae;
  color: white;
  cursor: pointer;
  transition: background 0.3s ease;
  font-size: 14px;
}

.sign-out-button:hover {
  background: #df824d;
}

.menu-wrapper {
  background: rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  height: calc(100vh - 280px);
  position: sticky;
  display: flex;
  flex-direction: column;
  overflow: auto;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.category-nav {
  position: sticky;
  top: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(10px);
  padding: 0.75rem 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 20;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px 8px 0 0;
}

.category-buttons-container {
  display: flex;
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  gap: 0.5rem;
  flex: 1;
  -webkit-overflow-scrolling: touch;
}

.category-buttons-container::-webkit-scrollbar {
  display: none;
}

.altercoin-conversion {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;
  background: rgba(118, 196, 174, 0.1);
  padding: 0.4rem 0.8rem;
  border-radius: 4px;
  border: 1px solid rgba(118, 196, 174, 0.3);
  margin-left: 0.5rem;
}

.altercoin-conversion p {
  margin: 0;
  font-size: 0.85rem;
  color: #76c4ae;
}

.category-nav-button {
  background: transparent;
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  font-size: 0.9rem;
  flex:1;
}

.category-nav-button:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.3);
}

.category-nav-button:active {
  background: rgba(118, 196, 174, 0.3);
}

.menu-section {
  flex: 1;
  overflow-y: auto;
  padding: 0 1.5rem 2rem 1.5rem;
  scrollbar-width: thin;
  scrollbar-color: rgba(118, 196, 174, 0.5) rgba(0, 0, 0, 0.2);
  z-index: 5;
  background: rgba(0, 0, 0, 0.5) !important;
  width: 100%;
  border-radius: 0 0 8px 8px;
}

.menu-section::-webkit-scrollbar {
  width: 6px;
}

.menu-section::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.2);
}

.menu-section::-webkit-scrollbar-thumb {
  background: rgba(118, 196, 174, 0.5);
  border-radius: 4px;
}

.menu-section::-webkit-scrollbar-thumb:hover {
  background: rgba(118, 196, 174, 0.7);
}

.category-section {
  margin-bottom: 2rem;
}

.category-section h2 {
  font-size: 1.3rem;
  color: #df824d;
  margin-bottom: 0.75rem;
  padding: 0.5rem 0;
  border-bottom: 2px solid #df824d;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.products-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 0.75rem;
}

.product-card {
  background: rgba(0, 0, 0, 0.5) !important;
  border: 1px solid rgba(118, 196, 174, 0.3);
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 0.75rem;
  border-radius: 6px;
  transition: all 0.2s ease;
  align-items: center;
}

.product-card:hover {
  border-color: rgba(118, 196, 174, 0.6);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.product-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  width: calc(100%);
}

.product-header {
  display: flex;
  flex-direction: column;
  margin-bottom: 0.25rem;
}

.product-name {
  font-weight: 600;
  font-size: 0.95rem;
  line-height: 1.2;
}

.product-price {
  color: #76c4ae;
  font-size: 0.9rem;
  font-weight: 500;
}

.product-status {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  flex-wrap: wrap;
  margin-top: 0.25rem;
}

.age-restriction {
  display: inline-block;
  background: #df824d;
  color: white;
  padding: 0.1rem 0.3rem;
  border-radius: 2px;
  font-size: 0.7rem;
  font-weight: 500;
  width: fit-content;
}

.age-restriction-string {
  display: inline-block;
  background: #c81414;
  color: white;
  padding: 0.1rem 0.3rem;
  border-radius: 2px;
  font-size: 0.7rem;
  font-weight: 500;
  width: fit-content;
}

.out-of-stock {
  display: inline-block;
  background: rgba(255, 69, 0, 0.2);
  color: #ff4500;
  padding: 0.1rem 0.3rem;
  border-radius: 2px;
  font-size: 0.7rem;
  font-weight: 500;
  width: fit-content;
}

.temporarily-unavailable {
  display: inline-block;
  background: rgba(255, 215, 0, 0.15);
  color: #ffd700;
  padding: 0.1rem 0.3rem;
  border-radius: 2px;
  font-size: 0.7rem;
  font-weight: 500;
  width: fit-content;
}

.stock-info {
  color: #76c4ae;
  font-size: 0.75rem;
}

.product-actions {
  display: flex;
  align-items: flex-start;
}

.add-button {
  padding: 0.4rem 0.75rem;
  background: #76c4ae;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.3s ease;
  font-size: 0.85rem;
  font-weight: 500;
}

.add-button:hover {
  background: #df824d;
}

.add-button:disabled {
  background: rgba(204, 204, 204, 0.3);
  cursor: not-allowed;
  color: rgba(255, 255, 255, 0.5);
}

@media (max-width: 980px) {
  .menu-section {
    padding: 0.5rem 1rem 50px 1rem;
  }
  
  .products-grid {
    grid-template-columns: 1fr;
  }
  
  .altercoin-conversion {
    width: 100%;
    display: flex;
    justify-content: center;
  }

  .category-nav {
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.5rem;
  }
  
  .category-buttons-container {
    width: 100%;
  }
  
  .altercoin-conversion {
    align-self: flex-end;
  }
}

@media (max-width: 535px) {
  /* .menu-wrapper {
    height: 71vh;
  } */

  .menu-section {
    padding: 0.5rem 0.5rem 50px 0.5rem;
  }

  .category-nav {
    padding: 0.75rem 0.5rem;
    gap: 0.25rem;
    width: 100%;
    position: sticky;
    top: 0;
    z-index: 30;

  }

  .category-nav-button {
    padding: 0.4rem 0.8rem;
    font-size: 0.8rem;
    white-space: nowrap;
  }
  
  .product-card {
    padding: 0.6rem;
  }
  
  .product-name {
    font-size: 0.9rem;
  }
}

.cart-section {
  background: rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #76c4ae;
  border-radius: 8px;
  padding: 1rem;
  height: calc(100vh - 280px);
  position: sticky;
  display: flex;
  flex-direction: column;
  overflow: auto;
}

.cart-container {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.cart-items {
  flex: 1;
  overflow-y: auto;
  margin: 1rem 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.cart-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem;
  margin: 0.2rem 0;
  background: rgba(0, 0, 0, 0.5) !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #76c4ae;
  border-radius: 4px;
}

.item-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.remove-button {
  padding: 0.25rem 0.5rem;
  background: #df824d;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.3s ease;
}

.remove-button:hover {
  background: #76c4ae;
}

.cart-summary {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #df824d;
}

.delivery-fee span {
  font-weight: 500;
}

.delivery-fee, .total {
  display: flex;
  justify-content: space-between;
  /* margin-bottom: 0.5rem; */
}

.total {
  font-weight: bold;
  font-size: 1.1em;
}

.delivery-toggle {
  margin: 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.delivery-toggle input[type="checkbox"] {
  width: 18px;
  height: 18px;
}

.delivery-toggle label {
  color: #000;
  font-weight: 500;
}

.order-button {
  width: 100%;
  padding: 0.75rem;
  background: #76c4ae;
  color: black;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-top: 1rem;
  transition: scale 0.3s ease;
}

.order-button:hover {
  scale: 1.05;
}

.order-button:disabled {
  background: #cccccc;
  cursor: not-allowed;
}

.stock-info {
  font-size: 0.9rem;
  color: #df824d;
  margin-top: 0.25rem;
}

.out-of-stock {
  color: #ff4444;
  font-weight: 500;
}

.temporarily-unavailable {
  color: #ff7300;
  font-weight: 500;
}

/* .age-restriction {
  display: inline-block;
  background: #df824d;
  color: white;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: bold;
  margin-top: 0.25rem;
} */

.quantity-input:disabled {
  background-color: #f0f0f0;
  cursor: not-allowed;
}

.user-id-input {
  margin: 1rem 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.comment-input {
  margin: 1rem 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.user-id-field {
  padding: 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(0, 0, 0, 0.3);
  color: white;
  border-radius: 4px;
  width: 100%;
}

.user-id-field::placeholder {
  color: rgba(255, 255, 255, 0.5);
}

.confirmation-dialog {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(109, 103, 103, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.dialog-content {
  background: rgba(0, 0, 0, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  padding: 2rem;
  border-radius: 8px;
  text-align: center;
  max-width: 90%;
  width: 400px;
  max-height: 80vh;
  overflow: scroll;
  position: relative;
}

.dialog-content p {
  margin-bottom: 1.5rem;
  color: white;
  font-size: 1.1rem;
}

.dialog-buttons {
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 2rem;
}

.confirm-button,
.cancel-button {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.confirm-button {
  background: #76c4ae;
  color: black;
}

.confirm-button:hover {
  background: #8dd6c0;
}

.cancel-button {
  background: #444;
  color: white;
}

.cancel-button:hover {
  background: #555;
}

.add-coins-button {
  position: absolute;
  top: 6rem;
  right: 1rem;
  background: #76c4ae;
  color: black;
  border: none;
  border-radius: 4px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 100;
  padding: 0.5rem 1rem;
  min-width: 170px;
}

.add-coins-button:hover {
  background: #8dd6c0;
}

.add-coins-dialog {
  width: 90%;
  max-width: 500px;
  padding: 2rem;
}

.add-coins-dialog .close-button {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  transition: all 0.2s ease;
  z-index: 10;
}

.add-coins-dialog .close-button:hover {
  background: rgba(223, 130, 77, 0.2);
  color: white;
}

.add-coins-dialog .close-button .v-icon {
  font-size: 20px;
}

.add-coins-dialog h2 {
  margin-top: 0;
  color: #76c4ae;
  text-align: center;
  margin-bottom: 1.5rem;
}

.add-coins-dialog h3 {
  color: white;
  margin: 1rem 0;
  font-size: 1rem;
}

.input-group {
  margin-bottom: 1.5rem;
  text-align: left;
}

.input-group label {
  display: block;
  margin-bottom: 0.5rem;
  color: white;
}

.coin-packages,
.payment-methods {
  margin-bottom: 1.5rem;
}

.package-buttons,
.method-buttons {
  display: grid;
  gap: 0.5rem;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  margin-top: 1rem;
}

.package-button,
.method-button {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 1rem;
  border-radius: 4px;
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.package-button:hover,
.method-button:hover {
  background: rgba(255, 255, 255, 0.2);
}

.package-button.active,
.method-button.active {
  background: #76c4ae;
  color: black;
}

.price {
  font-size: 0.9rem;
  opacity: 0.8;
}

.dialog-buttons {
  margin-top: 2rem;
}

.cart-category h3 {
  color: #df824d;
  font-size: 1rem;
  margin: 0.5rem 0;
}

.category-time {
  font-size: 0.6em;
  color: rgba(255, 255, 255, 1);
  margin-left: 0.2rem;
  font-weight: normal;
}

.supplements-list {
  font-size: 0.8em;
  margin-top: 0.25rem;
  color: rgba(255, 255, 255, 0.7);
}

.supplement-item {
  display: block;
  margin-left: 1rem;
}

.supplements-dialog {
  width: 500px;
}

.supplements-content {
  margin-top: 1.5rem;
}

.supplement-option {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 1rem;
  border-radius: 4px;
  margin: 0.5rem 0;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.2s ease;
}

.supplement-option:hover {
  background: rgba(255, 255, 255, 0.2);
}

.supplement-option.active {
  background: #76c4ae;
  color: black;
}

.supplement-name {
  font-weight: 500;
}

.supplement-price {
  font-size: 0.9em;
  opacity: 0.8;
}

.open-orders-container {
  background: rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  padding: 2rem;
  border-radius: 8px;
  position: absolute;
  right: 20px;
  top: 245px;
  overflow: visible !important;
  width: 400px;
  z-index: 100;
}

.order-card {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
}

.category-section {
  margin: 0;
}

.category-section h3 {
  color: #df824d;
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
}

.order-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 1rem;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.7);
}

.order-items {
  margin-bottom: 1rem;
}

.order-item {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  align-items: flex-start;
}

.item-quantity {
  color: rgba(255, 255, 255, 0.7);
  min-width: 2.5rem;
  font-weight: 500;
}

.item-details {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.item-name {
  font-weight: 500;
}

.supplements-list {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.6);
  margin-left: 0.5rem;
}

.supplement-item {
  display: block;
  font-style: italic;
}

.order-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 0.5rem;
  margin-top: 0.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.order-total {
  font-weight: 500;
  color: #76c4ae;
}

.order-status {
  font-size: 0.8rem;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  text-transform: uppercase;
  font-weight: 500;
}

.order-status.pending {
  background: #ffd700;
  color: #000;
}

.order-status.preparing {
  background: #00bfff;
  color: #000;
}

.order-status.completed {
  background: #32cd32;
  color: #000;
}

.order-status.cancelled {
  background: #ff4500;
  color: #fff;
}

.cart-actions {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.view-orders-button {
  position: absolute;
  top: 10rem;
  right: 1rem;
  background: #df824d;
  color: black;
  border: none;

  border-radius: 4px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 100;
  padding: 0.5rem 1rem;
  min-width: 170px;
}

.view-orders-button:hover {
  background: #c66f3e;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(109, 103, 103, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: rgba(0, 0, 0, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 8px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow: scroll;
  position: relative;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.close-button {
  background: none;
  border: none;
  color: white;
  font-size: 0.75rem;
  cursor: pointer;
  padding: 0.5rem;
  line-height: 1;
  display: none;
}

.orders-list {
  padding: 2rem;
  overflow-y: auto;
  max-height: calc(90vh - 70px);
}

.orders-modal .order-card:last-child {
  margin-bottom: 0;
}

.mobile-buttons {
  display: none;
}

@media (max-width: 980px) {
  .add-coins-button,
  .view-orders-button {
    display: none;
  }

  .mobile-buttons {
    display: flex;
    justify-content: center;
    gap: 1rem;
    margin-top: 2rem;
    width: auto;
    margin: 1rem 1rem 0 1rem;
    font-size: 0.7rem;
  }

  .mobile-buttons .add-coins-button,
  .mobile-buttons .view-orders-button {
    display: block;
    position: static;
    margin: 0;
    min-width: 0;
    flex: 1;
    max-width: 200px;
  }

  .catering-container {
    grid-template-columns: 1fr;
  }

  .category-buttons {
    flex-wrap: wrap;
    justify-content: center;
    gap: 0.5rem;
  }

  .category-button {
    font-size: 0.8rem;
    padding: 0.5rem 1rem;
  }

  .products-grid {
    grid-template-columns: 1fr;
  }

  .cart-toggle {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .cart-section {
    display: none;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100vh;
    z-index: 1000;
    background: rgba(109, 103, 103, 0.7);
    padding: 0;
  }

  .cart-section.is-open {
    display: block;
    animation: fadeIn 0.3s ease;
  }

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  .cart-section.is-open .cart-container {
    position: fixed;
    top: 55%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 92%;
    max-width: 420px;
    max-height: 85vh;
    background: rgba(15, 15, 15, 0.95);
    border: 1px solid rgba(118, 196, 174, 0.3);
    border-radius: 12px;
    padding: 1.25rem;
    box-shadow: 0 4px 25px rgba(0, 0, 0, 0.4);
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .cart-section.is-open .cart-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 0.75rem;
    border-bottom: 2px solid rgba(223, 130, 77, 0.5);
    margin-bottom: 0.5rem;
  }

  .cart-section.is-open .cart-header h2 {
    font-size: 1.4rem;
    color: #76c4ae;
    margin: 0;
  }

  .cart-section.is-open .cart-header span {
    font-size: 0.9rem;
    background: rgba(118, 196, 174, 0.15);
    padding: 0.3rem 0.6rem;
    border-radius: 4px;
    border: 1px solid rgba(118, 196, 174, 0.3);
  }

  .cart-section.is-open .cart-items {
    flex: 1;
    overflow-y: auto;
    padding: 0.5rem 0.25rem;
    margin: 0.5rem 0;
    max-height: calc(85vh - 280px);
    scrollbar-width: thin;
    scrollbar-color: rgba(118, 196, 174, 0.5) rgba(0, 0, 0, 0.2);
  }

  .cart-section.is-open .cart-items::-webkit-scrollbar {
    width: 5px;
  }

  .cart-section.is-open .cart-items::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 3px;
  }

  .cart-section.is-open .cart-items::-webkit-scrollbar-thumb {
    background: rgba(118, 196, 174, 0.5);
    border-radius: 3px;
  }

  .cart-section.is-open .cart-category h3 {
    font-size: 1rem;
    color: #df824d;
    margin: 0 0 0.5rem 0;
    padding-bottom: 0.15rem;
    border-bottom: 1px solid rgba(223, 130, 77, 0.3);
  }

  .cart-section.is-open .cart-item {
    padding: 0.6rem;
    margin: 0.4rem 0;
    background: rgba(30, 30, 30, 0.6);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    transition: all 0.2s ease;
  }

  .cart-section.is-open .cart-item:hover {
    background: rgba(40, 40, 40, 0.6);
    border-color: rgba(118, 196, 174, 0.3);
  }

  .cart-section.is-open .item-info {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
    flex: 1;
    padding-right: 0.5rem;
  }

  .cart-section.is-open .item-info > span:first-child {
    font-weight: 500;
    color: white;
  }

  .cart-section.is-open .item-info > span:last-child {
    font-size: 0.9rem;
    color: #76c4ae;
  }

  .cart-section.is-open .supplements-list {
    font-size: 0.8rem;
    color: rgba(255, 255, 255, 0.7);
    margin-left: 0.5rem;
  }

  .cart-section.is-open .remove-button {
    padding: 0.3rem 0.5rem;
    background: rgba(223, 130, 77, 0.8);
    color: white;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.8rem;
    transition: all 0.2s ease;
    white-space: nowrap;
  }

  .cart-section.is-open .remove-button:hover {
    background: #df824d;
  }

  .cart-section.is-open .cart-summary {
    background: rgba(20, 20, 20, 0.6);
    border-radius: 8px;
    padding: 1rem;
    margin-top: 0.5rem;
    border-top: none;
  }

  .cart-section.is-open .delivery-section {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.75rem;
    gap: 0.75rem;
  }

  .cart-section.is-open .delivery-section .delivery-fee {
    flex-shrink: 0;
  }

  .cart-section.is-open .delivery-section .seat-input {
    flex: 1;
  }

  .cart-section.is-open .delivery-fee label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: white;
    white-space: nowrap;
  }

  .cart-section.is-open .delivery-fee input[type="checkbox"] {
    width: 16px;
    height: 16px;
    accent-color: #76c4ae;
    margin: 0;
  }

  .cart-section.is-open .delivery-fee span {
    font-size: 0.85rem;
    color: rgba(255, 255, 255, 0.7);
  }

  .cart-section.is-open .seat-input .user-id-field {
    width: 100%;
    padding: 0.5rem;
    background: rgba(0, 0, 0, 0.3);
    border: 1px solid rgba(118, 196, 174, 0.3);
    color: white;
    border-radius: 6px;
    font-size: 0.9rem;
  }

  .cart-section.is-open .seat-input .user-id-field:focus {
    outline: none;
    border-color: rgba(118, 196, 174, 0.6);
    box-shadow: 0 0 0 2px rgba(118, 196, 174, 0.2);
  }

  .cart-section.is-open .seat-input .user-id-field.disabled-field {
    background-color: rgba(0, 0, 0, 0.2);
    color: rgba(255, 255, 255, 0.4);
    border-color: rgba(255, 255, 255, 0.1);
  }

  .disabled-field-indicator {
    display: flex;
    align-items: center;
    gap: 0.3rem;
    margin-top: -0.5rem;
    margin-bottom: 0.75rem;
    font-size: 0.75rem;
    color: rgba(255, 255, 255, 0.5);
  }

  .cart-section.is-open .total {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 1rem 0 0.5rem;
    padding-top: 0.75rem;
    border-top: 1px solid rgba(223, 130, 77, 0.5);
    font-weight: bold;
    font-size: 1.1rem;
  }

  .cart-section.is-open .total span:last-child {
    color: #76c4ae;
  }

  .cart-section.is-open .order-button {
    width: 100%;
    padding: 0.75rem;
    background: #76c4ae;
    color: black;
    font-weight: bold;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    margin-top: 0.75rem;
    transition: all 0.2s ease;
    font-size: 1rem;
  }

  .cart-section.is-open .order-button:hover:not(:disabled) {
    background: #8dd6c0;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  }

  .cart-section.is-open .order-button:disabled {
    background: rgba(204, 204, 204, 0.3);
    color: rgba(255, 255, 255, 0.5);
    cursor: not-allowed;
  }
  .catering-header h1 {
    font-family: 'Airborne GP', sans-serif;
    font-size: 1.8rem;
    font-weight: bold;
    text-transform: uppercase;
    color: #76c4ae;
    letter-spacing: 4px;
    position: relative;
    display: inline-block;
  }
  .catering-header {
    margin: 1.2rem 0 0 0;
  }
  .close-button {
    background: none;
    border: none;
    color: white;
    font-size: 0.75rem;
    cursor: pointer;
    padding: 0.5rem;
    line-height: 1;
    display: block;
  }
}

.notification-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  width: 100dvw;
  height: 100dvh;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.notification-prompt {
  background: rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  padding: 2rem;
  border-radius: 8px;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100dvw;
  height: 100dvh;
}

.notification-prompt h3 {
  color: #e69b71;
  margin-bottom: 1rem;
  font-size: 1.5rem;
}

.notification-prompt p {
  margin-bottom: 2rem;
  color: white;
}

.dismiss-button {
  background: #76c4ae;
  color: black;
  border: none;
  padding: 0.5rem 1.5rem;
  border-radius: 4px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s ease;
}

.dismiss-button:hover {
  background: #5fa892;
}

@media (max-width: 980px) {
  .notification-prompt {
    margin: 1rem;
    padding: 1.5rem;
  }
}

.disabled-field {
  background-color: rgba(0, 0, 0, 0.1);
  color: rgba(255, 255, 255, 0.6);
  cursor: not-allowed;
}

.disabled-field-indicator {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  margin-top: 0.3rem;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.6);
}

.disabled-field-indicator .v-icon {
  font-size: 0.8rem;
  opacity: 0.7;
}

.cart-section .wallet-balance span {
  font-size: 0.9rem;
  font-weight: 500;
  color: #76c4ae;
  padding: 0.4rem 0.7rem;
}

.delivery-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
  gap: 0.75rem;
}

#delivery-location {
  text-align: center;
}

.delivery-section .delivery-fee {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.delivery-section .delivery-fee label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: white;
  white-space: nowrap;
}

.delivery-section .delivery-fee input[type="checkbox"] {
  width: 16px;
  height: 16px;
  accent-color: #76c4ae;
  margin: 0;
}

.delivery-section .delivery-fee span {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.7);
}

.delivery-section .seat-input {
  flex: 1;
}

.delivery-section .seat-input .user-id-field {
  width: 100%;
  padding: 0.5rem;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(118, 196, 174, 0.3);
  color: white;
  border-radius: 6px;
  font-size: 0.9rem;
}

.delivery-section .seat-input .user-id-field:focus {
  outline: none;
  border-color: rgba(118, 196, 174, 0.6);
  box-shadow: 0 0 0 2px rgba(118, 196, 174, 0.2);
}

.delivery-section .seat-input .user-id-field.disabled-field {
  background-color: rgba(0, 0, 0, 0.2);
  color: rgba(255, 255, 255, 0.4);
  border-color: rgba(255, 255, 255, 0.1);
}

.disabled-field-indicator {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  margin-top: -0.5rem;
  margin-bottom: 0.75rem;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.5);
}

/* Mobile cart styles for delivery section */
.cart-section.is-open .delivery-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
  gap: 0.75rem;
}

.cart-section.is-open .delivery-section .delivery-fee {
  flex-shrink: 0;
}

.cart-section.is-open .delivery-section .seat-input {
  flex: 1;
}
</style>
